import { useCallback, useContext } from "react";

import { Notification } from "@snapchat/snap-design-system";

import { createNotificationError } from "./notifications";

interface ErrorConfig {
    path: string | number;
    errorCode: number;
    message?: string;
}

export interface NotificationApi {
    initialized: boolean;
    sendError: ({ path, errorCode, message }: ErrorConfig) => void;
    sendCustomError: (title: string, message?: string) => void;
    sendCustomSuccess: (title: string, message?: string) => void;
    sendCustomMessage: (title: string, message?: string) => void;
}

export const useNotificationApi = (): NotificationApi => {
    const notificationApi = useContext(Notification.Context);

    const sendError = useCallback(
        ({ path, errorCode, message }: ErrorConfig) => {
            const notificationError = createNotificationError(path, errorCode, message);
            if (notificationError) {
                notificationApi.error(notificationError);
            }
        },
        [notificationApi],
    );

    const sendCustomError = useCallback(
        (title: string, message?: string) => {
            notificationApi.error({
                title: title,
                message: message,
            });
        },
        [notificationApi],
    );

    const sendCustomSuccess = useCallback(
        (title: string, message?: string) => {
            notificationApi.success({
                title: title,
                message: message,
            });
        },
        [notificationApi],
    );

    const sendCustomMessage = useCallback(
        (title: string, message?: string) => {
            notificationApi.info({
                title: title,
                message: message,
            });
        },
        [notificationApi],
    );

    return {
        initialized: notificationApi.initialized,
        sendError,
        sendCustomError,
        sendCustomSuccess,
        sendCustomMessage,
    };
};
